import React, { useState, useRef, useEffect } from "react";
import { Trash2, Plus, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import SearchableDropdown from "./components/SearchableDropdown";

type ItemsEditorInterfaceT = {
  items: string;
  description: string;
  quantity: string;
  unit: string;
  amount: string;
  pricePerUnit: string;
  total: string;
  addItem: string;
  manageSavedItems?: string;
  descriptionPlaceholder?: string;
  unitPlaceholder?: string;
  units?: any;
};

// Local Unit Selector component for combobox behavior
function UnitSelector({
  value,
  onChange,
  placeholder,
  units,
  className,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  units: any;
  className: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const options =
    units && typeof units === "object" ? Object.values(units) : [];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative group">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className={`${className} pr-8`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
        >
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      <AnimatePresence>
        {isOpen && options.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.1 }}
            className="absolute z-[110] left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden py-1 max-h-[200px] overflow-y-auto custom-scrollbar"
          >
            {options.map((val: any, idx: number) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  onChange(val);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                  value === val
                    ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-bold"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                }`}
              >
                {val}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type Item = {
  id: string;
  description: string;
  quantity?: number;
  unit?: string;
  amount?: number;
};

type Props = {
  items: Item[];
  currency: string;
  interfaceT: ItemsEditorInterfaceT;
  updateItem: (id: string, patch: Partial<Item>) => void;
  removeItem: (id: string) => void;
  addItem: () => void;
  getAmountDisplayValue: (id: string, amount?: number) => string;
  handleAmountChange: (id: string, value: string) => void;
  showErrors?: boolean;
  getValidationErrors?: () => string[];
  savedItems?: Item[];
  onManageSavedItems?: () => void;
  onSelectSavedItem?: (item: any) => void;
};

export default function ItemsEditor(props: Props) {
  const {
    items,
    interfaceT,
    updateItem,
    removeItem,
    addItem,
    getAmountDisplayValue,
    handleAmountChange,
    showErrors,
    getValidationErrors,
    savedItems = [],
    onManageSavedItems,
    onSelectSavedItem,
  } = props;

  const getInputClass = (isError: boolean, baseClass: string) => {
    return `${baseClass} ${
      showErrors && isError
        ? "border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/10"
        : ""
    }`;
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          <div className="flex-1">
            <SearchableDropdown
              options={savedItems.map((item) => ({
                value: item.id,
                label: item.description,
                sublabel: `${item.amount} ${item.unit || ""}`,
              }))}
              value=""
              onChange={(val) => {
                const item = savedItems.find((i) => i.id === val);
                if (item && onSelectSavedItem) onSelectSavedItem(item);
              }}
              placeholder="Select from saved items"
              searchPlaceholder="Search saved items..."
              noOptionsText="No saved items found"
              iconType="package"
              renderOption={(option: any) => {
                const item = savedItems.find(i => i.id === option.value);
                return (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium truncate">{option.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                        {option.sublabel}
                      </span>
                      {item && (item as any).pricePerUnit > 0 && (
                        <span className="text-[10px] text-emerald-500 font-bold">
                          Unit: {(item as any).pricePerUnit}
                        </span>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          </div>
          {onManageSavedItems && (
            <button
              type="button"
              onClick={onManageSavedItems}
              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors dark:bg-indigo-600 dark:hover:bg-indigo-700 whitespace-nowrap flex-shrink-0"
            >
              {interfaceT.manageSavedItems || "Manage saved items"}
            </button>
          )}
        </div>
      </div>

      {/* Desktop table view - hidden on small screens */}
      <div className="hidden min-[680px]:block w-full rounded-2xl border border-slate-200 dark:border-slate-700 mb-4 shadow-sm overflow-visible">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 uppercase text-[10px] tracking-wider">
              <th className="py-3 pl-4 pr-2 font-bold w-[38%] rounded-tl-2xl">
                {interfaceT.description}
              </th>
              <th className="py-3 px-2 font-bold w-[16%] text-center whitespace-nowrap">
                {interfaceT.quantity}
              </th>
              <th className="py-3 px-2 font-bold w-[16%] text-center whitespace-nowrap">
                {interfaceT.unit}
              </th>
              <th className="py-3 px-2 font-bold w-[22%] text-right whitespace-nowrap">
                {interfaceT.amount}
              </th>
              <th className="py-3 pl-2 pr-4 w-[8%] rounded-tr-2xl"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 overflow-visible">
            {items.map((it, index) => (
              <tr 
                key={it.id} 
                className="group bg-white dark:bg-slate-800"
                style={{ position: 'relative', zIndex: items.length - index }}
              >
                <td className="py-3 pl-4 pr-2 align-top break-all">
                  <div 
                    className="auto-expand-wrapper"
                    data-replicated-value={it.description}
                  >
                    <textarea
                      value={it.description}
                      rows={1}
                      onChange={(e) =>
                        updateItem(it.id, { description: e.target.value })
                      }
                      placeholder={
                        interfaceT.descriptionPlaceholder ||
                        "Service / product description"
                      }
                      className={getInputClass(
                        !it.description?.trim(),
                        "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                      )}
                    />
                  </div>
                </td>
                <td className="py-3 px-2 align-top">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={it.quantity ?? ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(",", ".");
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        if (value === "") {
                          updateItem(it.id, { quantity: undefined });
                        } else {
                          const num = parseFloat(value);
                          if (!isNaN(num)) {
                            updateItem(it.id, { quantity: num });
                          }
                        }
                      }
                    }}
                    placeholder="1"
                    autoComplete="off"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-center"
                  />
                </td>
                <td className="py-3 px-2 align-top relative">
                  <UnitSelector
                    value={it.unit ?? ""}
                    onChange={(val) =>
                      updateItem(it.id, { unit: val || undefined })
                    }
                    placeholder={interfaceT.unitPlaceholder || "pcs"}
                    units={interfaceT.units}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-center"
                  />
                </td>
                <td className="py-3 px-2 align-top">
                  <input
                    type="text"
                    value={getAmountDisplayValue(it.id, it.amount)}
                    onChange={(e) => handleAmountChange(it.id, e.target.value)}
                    placeholder="0.00"
                    className={getInputClass(
                      !it.amount || it.amount <= 0,
                      "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-right"
                    )}
                  />
                </td>
                <td className="py-3 pl-2 pr-4 align-top text-right">
                  <button
                    onClick={() => removeItem(it.id)}
                    className="p-2 text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                    title="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view - shown on screens < 680px */}
      <div className="min-[680px]:hidden space-y-3 mb-4 overflow-visible">
        {items.map((it, index) => (
          <div
            key={it.id}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-3 shadow-sm relative"
            style={{ zIndex: items.length - index }}
          >
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                {interfaceT.description}
              </label>
              <div 
                className="auto-expand-wrapper"
                data-replicated-value={it.description}
              >
                <textarea
                  value={it.description}
                  rows={1}
                  onChange={(e) =>
                    updateItem(it.id, { description: e.target.value })
                  }
                  placeholder={
                    interfaceT.descriptionPlaceholder ||
                    "Service / product description"
                  }
                  className={getInputClass(
                    !it.description?.trim(),
                    "w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  )}
                />
              </div>
            </div>
              <div className="grid grid-cols-2 min-[390px]:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {interfaceT.quantity}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={it.quantity ?? ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(",", ".");
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        if (value === "") {
                          updateItem(it.id, { quantity: undefined });
                        } else {
                          const num = parseFloat(value);
                          if (!isNaN(num)) {
                            updateItem(it.id, { quantity: num });
                          }
                        }
                      }
                    }}
                    placeholder="1"
                    autoComplete="off"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-center"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {interfaceT.unit}
                  </label>
                  <UnitSelector
                    value={it.unit ?? ""}
                    onChange={(val) =>
                      updateItem(it.id, { unit: val || undefined })
                    }
                    placeholder={interfaceT.unitPlaceholder || "pcs"}
                    units={interfaceT.units}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-center"
                  />
                </div>
                <div className="col-span-2 min-[390px]:col-span-1 space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {interfaceT.amount}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={getAmountDisplayValue(it.id, it.amount)}
                      onChange={(e) => handleAmountChange(it.id, e.target.value)}
                      placeholder="0.00"
                      autoComplete="off"
                      className={getInputClass(
                        !it.amount || it.amount <= 0,
                        "flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-right"
                      )}
                    />
                    <button
                      onClick={() => removeItem(it.id)}
                      className="p-2 text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-xl transition-colors flex-shrink-0"
                      title="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
      >
        <Plus className="w-4 h-4" />
        {interfaceT.addItem}
      </button>
    </div>
  );
}
